import { DateTime } from 'luxon'
import type { Admin } from './admin'

export interface UserJson {
    id: number
    name: string
    display_name?: string | null
    first_name: string
    last_name: string
    email: string
    email_verified_at: string | null
    contact_number: string
    contact_number_verified_at: string | null
    image?: string
    // UserTransformer sends the profile photo as `avatar_url`
    avatar_url?: string
    userable: {
        data: unknown
    }
    user_type_id: number | null
    user_type: string | null
    work_status?: string | null
    roles?: string[]
    has_account?: boolean

    provisioned?: boolean
}

export class User {
    id: number
    firstName: string
    lastName: string
    displayName?: string
    email: string
    emailVerifiedAt: DateTime | null
    contactNumber: string
    contactNumberVerifiedAt: DateTime | null
    image: string
    avatarUrl: string
    userable: unknown
    workStatus: string | null = null

    userTypeId: number | null = null
    userType: string | null = null

    admin: Admin | null = null
    candidate: unknown | null = null

    roles: readonly string[] = []

    hasAccount: boolean = false

    provisioned: boolean = false

    constructor (
        id: number,
        firstName: string,
        lastName: string,
        email: string,
        emailVerifiedAt: DateTime | null,
        contactNumber: string,
        contactNumberVerifiedAt: DateTime | null,
        image: string,
        avatarUrl: string,
        userable: unknown,
        userTypeId: number | null,
        userType: string | null,
        workStatus: string | null = null,
        roles: string[] = [],
        hasAccount: boolean = false,
        provisioned: boolean = false
    ) {
        this.id = id

        this.firstName = firstName
        this.lastName = lastName

        this.displayName = `${firstName} ${lastName}`

        this.email = email
        this.emailVerifiedAt = emailVerifiedAt

        this.contactNumber = contactNumber
        this.contactNumberVerifiedAt = contactNumberVerifiedAt

        this.image = image
        this.avatarUrl = avatarUrl

        this.userable = userable

        this.userTypeId = userTypeId
        this.userType = userType

        this.workStatus = workStatus

        if (userType === 'admin') {
            this.admin = null
        } else if (userType === 'candidate') {
            this.candidate = null
        }

        this.roles = roles

        this.hasAccount = hasAccount

        this.provisioned = provisioned
    }

    static fromJson(json: UserJson): User {
        const user = new User(
            json.id,
            json.first_name,
            json.last_name,
            json.email,
            json.email_verified_at ? DateTime.fromISO(json.email_verified_at) : null,
            json.contact_number,
            json.contact_number_verified_at ? DateTime.fromISO(json.contact_number_verified_at) : null,
            json.avatar_url ?? json.image ?? '',
            json.avatar_url ?? json.image ?? '',
            json.userable?.data,
            json.user_type_id,
            json.user_type,
            json.work_status,
            json.roles ?? [],
            json.has_account ?? false,
            json.provisioned ?? false
        )

        // Self-registered users only have `name`, so first/last are null; prefer the server's display name
        if (json.display_name) {
            user.displayName = json.display_name
        }

        return user
    }
}
